import React, { useContext } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { useNavigate } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "../../../../catalyst-components/button";
import { Id } from "../../../../convex/_generated/dataModel";
import { EmbeddingConfig } from '../../../../shared/models';
import { Alert, AlertActions, AlertDescription, AlertTitle } from "../../../../catalyst-components/alert";
import { BaseContext } from '../../../../shared/base-context';


export const EmbeddingsConfig: React.FC = () => {
    const navigate = useNavigate();
    const { convexUserId } = useContext(BaseContext);
    const configs = useQuery(api.embeddings_config.list);
    const updateConfig = useMutation(api.embeddings_config.update);
    const [pendingChanges, setPendingChanges] = React.useState<Record<string, number>>({});
    const [showAlert, setShowAlert] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState('');
    const [success, setSuccess] = React.useState(false);

    const handleThresholdChange = (config: EmbeddingConfig, value: string) => {
        const numValue = parseFloat(value);
        setPendingChanges(prev => ({
            ...prev,
            [config._id]: numValue
        }));
    };

    const handleSave = async () => {
        try {
            for (const [id, threshold] of Object.entries(pendingChanges)) {
                const config = configs?.find(c => c._id === id);
                if (config) {
                    await updateConfig({
                        id: id as Id<"embeddings_config">,
                        name: config.name,
                        modified_by: convexUserId as Id<"users">,
                        description: config.description,
                        cosine_similarity_threshold: threshold
                    });
                }
            }
            setPendingChanges({});
            setSuccess(true);
            setAlertMessage('Configurations updated successfully!');
            setShowAlert(true);
        } catch (error) {
            setSuccess(false);
            setAlertMessage('Error updating configurations. Please try again.');
            setShowAlert(true);
        }
    };

    if (!configs) {
        return <div className="min-h-screen bg-background pt-24 p-6">Loading...</div>;
    }

    const hasChanges = Object.keys(pendingChanges).length > 0;

    return (
        <div className="min-h-screen bg-background pt-24 px-6 py-4">
            <div className="flex items-center gap-4 mb-6">
                <Button color="light" onClick={() => navigate('../')}>
                    Go back
                </Button>
                <h2 className="text-xl font-bold">Embedding Configurations</h2>
            </div>

            <div className="space-y-4">
                {configs.map(config => (
                    <div key={config._id} className="border rounded-lg p-4 bg-white shadow">
                        <h3 className="font-semibold mb-2">{config.name}</h3>
                        <div className="flex items-center gap-4">
                            <label className="text-sm text-gray-600 min-w-[120px]">
                                Similarity Threshold:
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={pendingChanges[config._id] ?? config.cosine_similarity_threshold}
                                onChange={(e) => handleThresholdChange(config, e.target.value)}
                                className="flex-1"
                            />
                            <span className="text-sm min-w-[50px]">
                                {(pendingChanges[config._id] ?? config.cosine_similarity_threshold).toFixed(2)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 flex justify-end">
                <Button
                    color="light"
                    disabled={!hasChanges}
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            </div>

            <Alert open={showAlert} onClose={() => setShowAlert(false)} size="lg">
                <AlertTitle>{success ? 'Success' : 'Error'}</AlertTitle>
                <AlertDescription>
                    {alertMessage}
                </AlertDescription>
                <AlertActions>
                    <Button plain onClick={() => setShowAlert(false)}>
                        Ok
                    </Button>
                </AlertActions>
            </Alert>
        </div>
    );
};
